import isEmpty from 'lodash/isEmpty';

import {v8nSchemaOfGetEventsResponse} from '@entities/event/api/backendSchemas';
import {
  EventListItemOnBackend,
  GetEventsResponse,
} from '@entities/event/api/backendTypes';
import {mapEventFromBackend} from '@entities/event/api/dataMappers';
import {Event, EventStatus} from '@entities/event/types';
import {
  axi,
  createBackendDateTimeString,
  getBackendAssetPath,
  GetListOutput,
  GetListParams,
} from '@shared/api';
import {TranslationDict} from '@shared/types';
import {validateData} from '@shared/utils';
import {getCategories} from '../../category';
import {Topic} from '../types';
import {topicV8nSchema, v8nSchemaOfGetTopicsResponse} from './backendSchemas';
import {GetTopicsResponse, TopicOnBackend} from './backendTypes';
import {mapTopicFromBackend} from './dataMappers';

export interface GetTopicsParams extends GetListParams {
  isLive?: boolean;
  isPromoted?: boolean;
  categoryId?: string;
  searchQuery?: string;
  subCategoryIds?: string[] | string;
  geoFilterIds?: string[];
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  eventStatuses?: EventStatus[];
}

export interface GetTopicEventsParams extends GetListParams {
  topicId: string;
  section?: string | null;
  isFavorite?: boolean | null;
}

export interface UpdateTopicParams {
  topicId: string;
  nameTrans: TranslationDict;
  shortName: string;
  logoUrl: string;
  bannerUrl: string;
  streamUrl: string | null;
  subCategorySlugs: string[];
  startDate: Date;
  endDate?: Date | null;
  isLive: boolean;
  isFavorite?: boolean;
  geoSlugs: string[];
  autoPublishEnabled: boolean;
}

export interface CreateTopicParams {
  slug: string;
  nameTrans: TranslationDict;
  shortName: string;
  logoUrl: string;
  bannerUrl: string;
  categorySlug?: string;
  subcategorySlug?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  geoSlugs: string[];
  autoPublishEnabled: boolean;
}

export const getTopics = async ({
  page,
  pageSize,
  isLive,
  isPromoted,
  categoryId,
  searchQuery,
  subCategoryIds,
  geoFilterIds,
  sortField = 'created_at',
  sortOrder = 'desc',
  eventStatuses,
}: GetTopicsParams = {}): Promise<GetListOutput<Topic>> => {
  const subCategoryIdsFinal =
    typeof subCategoryIds === 'string'
      ? [subCategoryIds]
      : isEmpty(subCategoryIds)
        ? undefined
        : subCategoryIds;

  const response = await axi.get<GetTopicsResponse>('admin/topics', {
    params: {
      page,
      per_page: pageSize,
      live: isLive || undefined,
      promoted: isPromoted,
      search_query: searchQuery || undefined,
      category: categoryId || undefined,
      subcategories: subCategoryIdsFinal,
      geo_filter: isEmpty(geoFilterIds) ? undefined : geoFilterIds,
      sort_field: sortField,
      sort_order: sortOrder,
      event_statuses: isEmpty(eventStatuses) ? undefined : eventStatuses,
    },
    paramsSerializer: {
      // no brackets at all for the following query string - ?page=1&per_page=9&type_slugs[]=INV&type_slugs[]=TBP
      indexes: null,
    },
  });

  try {
    validateData(response.data, v8nSchemaOfGetTopicsResponse, 'getTopics');
  } catch {
    //
  }

  const {
    array,
    total,
    total_pages,
    page: pageFromBackend,
    per_page,
  } = response.data;
  const items = array?.map(item => mapTopicFromBackend(item)) ?? [];

  return {
    items,
    page: pageFromBackend,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

export const getTopic = async (id: string): Promise<Topic> => {
  const [topicResponse, categoriesResponse] = await Promise.allSettled([
    axi.get<TopicOnBackend>(`/admin/topics/${id}`),
    getCategories(),
  ]);

  if (topicResponse.status === 'rejected') {
    throw topicResponse.reason;
  }

  const response = topicResponse.value;
  const categories =
    categoriesResponse.status === 'fulfilled' ? categoriesResponse.value : [];

  try {
    validateData(response.data, topicV8nSchema, 'getTopic');
  } catch {
    //
  }

  return mapTopicFromBackend(response.data, categories);
};

export const getTopicEvents = async ({
  topicId,
  page,
  pageSize,
}: GetTopicEventsParams): Promise<GetListOutput<Event>> => {
  const [eventsResponse, categoriesResponse] = await Promise.allSettled([
    axi.get<GetEventsResponse>(`/admin/topics/${topicId}/events`, {
      params: {
        page,
        per_page: pageSize,
      },
    }),
    getCategories(),
  ]);

  if (eventsResponse.status === 'rejected') {
    throw eventsResponse.reason;
  }

  const response = eventsResponse.value;
  const categories =
    categoriesResponse.status === 'fulfilled' ? categoriesResponse.value : [];

  try {
    validateData(response.data, v8nSchemaOfGetEventsResponse, 'getTopicEvents');
  } catch {
    //
  }

  const {
    array,
    total,
    total_pages,
    page: pageFromBackend,
    per_page,
  } = response.data;

  const items =
    array?.map((item: EventListItemOnBackend) =>
      mapEventFromBackend(item, categories),
    ) ?? [];

  return {
    items,
    page: pageFromBackend,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

export const updateTopic = async ({
  topicId,
  nameTrans,
  shortName,
  logoUrl,
  bannerUrl,
  streamUrl,
  subCategorySlugs,
  startDate,
  endDate,
  isLive,
  isFavorite,
  geoSlugs,
  autoPublishEnabled,
}: UpdateTopicParams): Promise<void> => {
  const cleanedNameTrans: Partial<TranslationDict> = {};

  Object.entries(nameTrans).forEach(([lang, trans]) => {
    if (typeof trans === 'string' && trans.trim()) {
      cleanedNameTrans[lang as keyof TranslationDict] = trans;
    }
  });

  const payload = {
    name: cleanedNameTrans,
    short_name: shortName,
    logo_url: getBackendAssetPath(logoUrl),
    banner_url: getBackendAssetPath(bannerUrl),
    stream_url: streamUrl || null,
    subcategory_slugs: subCategorySlugs,
    start_date: createBackendDateTimeString(startDate),
    end_date: createBackendDateTimeString(endDate ?? startDate),
    is_live: isLive,
    is_favorite: isFavorite ?? false,
    geo_slugs: geoSlugs,
    auto_publish_enabled: autoPublishEnabled,
  };

  await axi.put(`/admin/topics/${topicId}`, payload);
};

export const createTopic = async ({
  slug,
  nameTrans,
  shortName,
  logoUrl,
  bannerUrl,
  categorySlug,
  subcategorySlug,
  startDate,
  endDate,
  geoSlugs,
  autoPublishEnabled,
}: CreateTopicParams): Promise<void> => {
  const cleanedNameTrans: Partial<TranslationDict> = {};

  Object.entries(nameTrans).forEach(([lang, trans]) => {
    if (typeof trans === 'string' && trans.trim()) {
      cleanedNameTrans[lang as keyof TranslationDict] = trans;
    }
  });

  const now = createBackendDateTimeString(new Date());

  const payload = {
    slug,
    name: cleanedNameTrans,
    short_name: shortName || undefined,
    logo_url: logoUrl ? getBackendAssetPath(logoUrl) : undefined,
    banner_url: bannerUrl ? getBackendAssetPath(bannerUrl) : undefined,
    start_date: startDate ? createBackendDateTimeString(startDate) : now,
    end_date: endDate ? createBackendDateTimeString(endDate) : now,
    category_slug: categorySlug || undefined,
    subcategory_slug: subcategorySlug || undefined,
    geo_slugs: geoSlugs,
    auto_publish_enabled: autoPublishEnabled,
  };

  await axi.post('/admin/topics', payload);
};

export const refreshTopicEvents = async (topicSlug: string): Promise<void> => {
  await axi.post(
    `/admin/topics/${encodeURIComponent(topicSlug)}/events/refresh`,
  );
};

export const updateTopicParticipants = async (
  topicSlug: string,
  participantIds: string[],
): Promise<void> => {
  await axi.put(`/admin/topics/${topicSlug}/participants`, {
    participant_ids: participantIds,
  });
};
